import { createContext, useContext } from 'react';
import { Session, User } from '@supabase/supabase-js';
import type { AccountSwitcherApi } from '@/hooks/useAccountSwitcher';

export type SubscriptionLevel = 'standard' | 'pro';
export type PlanLevel = SubscriptionLevel | 'free';

export type BillingStatus = {
  user: {
    hasTrialed: boolean;
  };
  subscription: {
    level: SubscriptionLevel;
    status: string | null;
    currentPeriodEnd: string | null;
  } | null;
  tokens: {
    free: number;
    subscription: number;
    purchased: number;
    total: number;
  };
};

const ACTIVE_STATUSES = new Set(['active', 'trialing']);

export function getLevel(billing: BillingStatus | null | undefined): PlanLevel {
  if (!billing?.subscription) return 'free';
  if (!ACTIVE_STATUSES.has(billing.subscription.status ?? '')) return 'free';
  return billing.subscription.level;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  billing: BillingStatus | null;
  // True until billing/profile are ALSO loaded. Use this to gate UI that needs
  // token balances / subscription level (limit messages, credit counts) so they
  // don't flash a wrong/zero state.
  isLoading: boolean;
  // True only until auth/session itself resolves — does NOT wait on billing or
  // profile. Use this to gate the app SHELL (Layout, AuthGuard) so the viewer
  // and data queries render as soon as the user is known, instead of blocking
  // ~1s+ on the billing-status edge fn. See issue #58.
  isAuthLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  // Upgrade the CURRENT anonymous session to a permanent email/password account
  // in place — same user_id, so the guest's conversations/meshes/storage are all
  // retained (vs. signUp, which mints a fresh user and orphans the guest's work).
  convertAnonymousAccount: (
    email: string,
    password: string,
    name: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  // Multi-account switching (#297). Lets the UI list other signed-in accounts,
  // switch between them, add a new one, and sign out of one or all. Anonymous
  // sessions are never stored as switchable accounts.
  accounts: AccountSwitcherApi;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
